import React, {
  FC,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { Pojo } from "types/Galaxy";
import Axios from "axios";
import { DatatableSort } from "composants/datatable/Table";
import { TypeSimpleComponent } from "enum";
import { ComponentReadOnly, ComponentState, Item } from "types/Component";
import { AutoSizer } from "react-virtualized";
import ConfirmationModal from "composants/processus/ConfirmationModal";
import { ShowConfirmModalProps } from "composants/datatableAGGrid/menuItem/ProcessMenuItem";
import { FilterBar } from "types/Search";
import {
  ColDef,
  CustomEditorModule,
  GetRowIdParams,
  GridReadyEvent,
  IServerSideGetRowsParams,
  ModuleRegistry,
  RowClickedEvent,
  RowStyleModule,
  RowClassRules,
  ValueSetterParams,
  CellClassParams,
  LargeTextEditorModule,
  FilterModel,
  CellEditingStoppedEvent,
  TooltipModule,
  ColumnApiModule,
  ScrollApiModule,
  SortModelItem,
  ColumnState,
  ColumnPinnedEvent,
  SortChangedEvent,
  BodyScrollEvent,
  RowApiModule,
  PinnedRowModule,
  RowHeightParams,
  SuppressKeyboardEventParams,
  CellKeyDownEvent,
  ProcessCellForExportParams,
  SelectionColumnDef,
  SELECTION_COLUMN_ID,
  isColumnSelectionCol,
} from "ag-grid-community";
import {
  SetFilterModule,
  ColumnMenuModule,
  ContextMenuModule,
  MultiFilterModule,
  ValidationModule,
  DateFilterModule,
  NumberFilterModule,
  TextFilterModule,
  ServerSideRowModelModule,
  LocaleModule,
  GetMainMenuItemsParams,
  GetContextMenuItemsParams,
  MenuItemDef,
  CellSelectionModule,
  RowSelectionModule,
  RowSelectionOptions,
  SelectionChangedEvent,
  ServerSideRowModelApiModule,
  CellStyleModule,
  TextEditorModule,
  NumberEditorModule,
  INumberCellEditorParams,
  DateEditorModule,
  RowGroupingModule,
  RichSelectModule,
  ClipboardModule,
} from "ag-grid-enterprise";

import {
  AG_GRID_LOCALE_FR,
  AG_GRID_LOCALE_DE,
  AG_GRID_LOCALE_IT,
  AG_GRID_LOCALE_EN,
} from "@ag-grid-community/locale";

import { tw } from "twind";
import { Fa } from "composants/Icon";
import MenuItem from "./menuItem/ProcessMenuItem";
import { getProcessusDefinition, getProcessusDefinitionColumn } from "api/processus";
import MiniExpertMenuItem from "./menuItem/MiniExpertMenuItem";
import InfoMenuItem from "./menuItem/InfoMenuItem";
import { useTranslation } from "react-i18next";
import { ProcessusProvider } from "composants/processus/ProcessusProvider";
import { track } from "tracking";
import { existList } from "api/satellites";
import useInteractions from "hooks/useInteractions";
import AgTableToolbar from "./GridDtToolbar";
import { parseSortStr } from "containers/datatable/datatableReducer";
import { buildCellEditorAutocomplete } from "./cellEditor/CellEditorAutocomplete";
import { buildCellEditorAutocompleteView } from "./cellEditor/CellEditorAutocompleteView";
import { buildCellEditorAutocompleteViewFree } from "./cellEditor/CellEditorAutocompleteViewFree";
import { buildCellEditorCalendar } from "./cellEditor/CellEditorCalendar";

import "./AgGrid.css";
import CellEditorRichText from "./cellEditor/CellEditorRichText";
import { HighlightProperties, isHighlighted } from "composants/datatable/dialog/HighlightDialog";
import { useDispatch } from "react-redux";
import { contextualize } from "actions/contextTreeModule";
import { getGridDtKey, GridDtCacheState, GRID_DT_CACHE } from "./hoc/withGridDtCache";
import buildAutocompleteCellRenderer from "./cellRenderer/AutocompleteCellRenderer";
import buildAutocompleteViewCellRenderer from "./cellRenderer/AutocompleteViewCellRenderer";
import { uuidv4 } from "utils/uuid.utils";
import { useRowMaxHeight, RowHeightContext } from "hooks/useRowMaxHeight";
import { HtmlPreviewProvider } from "./cellRenderer/HtmlCellRenderer";
import { GroupByFunctionParameter, AggregateProperties } from "types/InteractiveReport";
import {
  buildBooleanCellRenderer,
  buildHtmlRenderer,
  buildLinkRenderer,
  buildOelRenderer,
  buildProcessusLinkRenderer,
  buildSysDomaineIRRenderer,
  buildTagRenderer,
  CheckBoxCellEditor,
  CheckBoxRenderer,
  CheckboxFilterRenderer,
} from "./cellRenderer/CellRenderers";
import { SateliteMenuRenderer } from "./cellRenderer/SateliteMenuRenderer";
import { AGFilter, DatatableAGGridProps } from "./Grid.types";
import { DatatableFocusState } from "types/Focus";
import { explainFilter, getFilterAllField, getGroupByFilter } from "./utils/queryUtils";
import { RSQLCriteria } from "rsql-criteria-typescript";
import { getCalendarColumnProps } from "./utils/dateUtils";
import { formatCellValueForClipboard } from "./utils/clipboardUtils";

export const MIN_BATCH_SIZE = 25;
const MIN_ROW_HEIGHT = 40;
// Hauteur de repli avant la première mesure réelle (cf. .datatable-toolbar { height: 60px }).
const DEFAULT_TOOLBAR_HEIGHT = 60;

ModuleRegistry.registerModules([
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  CellSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  DateFilterModule,
  LocaleModule,
  MultiFilterModule,
  NumberFilterModule,
  SetFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
  CellStyleModule,
  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CustomEditorModule,
  RowGroupingModule,
  RowStyleModule,
  LargeTextEditorModule,
  TooltipModule,
  RichSelectModule,
  ColumnApiModule,
  ScrollApiModule,
  RowApiModule,
  PinnedRowModule,
  ClipboardModule,
]);

const ROW_SELECTION_MODE: RowSelectionOptions | "single" | "multiple" = {
  mode: "multiRow",
  headerCheckbox: false,
  isRowSelectable: (rowNode) => !rowNode.group,
};

// Référence stable obligatoire : un objet recréé à chaque rendu déclenche côté AG Grid
// un applyColumnState + animation des colonnes qui plante si la grille est recréée
// (masquage/réaffichage par Suspense) avant le montage de son body (setColumnMovingCss).
//
// lockPosition la place déjà en tête de la liste, mais sans pin elle reste dans la zone
// centrale : toute colonne épinglée à gauche s'affiche alors avant elle.
// Le pin est aussi réappliqué dans applyColumnState, car defaultState.pinned = null
// dépinglerait cette colonne si elle est absente de l'état restauré.
const SELECTION_COLUMN_DEF: SelectionColumnDef = {
  pinned: "left",
  lockPinned: true,
  lockPosition: "left",
};

function withSelectionColumnPinned<T extends { colId?: string | null }>(state: T[]): T[] {
  return [
    ...state.filter((item) => !item.colId || !isColumnSelectionCol(item.colId)),
    { colId: SELECTION_COLUMN_ID, pinned: "left" } as any,
  ];
}

function defaultGgetRowId(params: GetRowIdParams) {
  return params.data.id ?? params.data.uuid;
}

function getValueFromSd(p: { value: Item }) {
  return p.value.value;
}

function getLabelFromSd(p: { value: Item }) {
  return p.value.label;
}

function getNumberEditorParamsFromScale(
  scale?: number,
): Pick<INumberCellEditorParams, "precision" | "step"> {
  if (scale == null || scale < 0) {
    return { step: 1 };
  }
  return {
    precision: scale,
    step: Math.pow(10, -scale),
  };
}

function getPropsByTypeCompo({
  typeCompo,
  tableName,
  sjmoCode,
  ctrlKey,
  componentID,
  columnIndex,
  compo,
  onChange,
  onAfterSaveProcess,
  booleanLabels,
}: {
  typeCompo: string;
  tableName: string;
  sjmoCode: string;
  ctrlKey: string;
  componentID: string;
  columnIndex: number;
  compo: ComponentState;
  onChange?: (params: ValueSetterParams<Pojo, string>) => void;
  onAfterSaveProcess?: () => void;
  booleanLabels?: { yes: string; no: string };
}): {
  filter: string | boolean;
  filterParams?: Record<string, any>;
  filterValues?: any[];
  renderer?: FC<any>;
  filterRenderer?: FC<any>;
  cellEditor?: string | FC<any>;
  cellEditorPopup?: boolean;
  cellEditorParams?: Record<string, any>;
  cellStyle?: Record<string, any>;
  cellDataType?: "dateString" | "dateTimeString";
  outputIso?: boolean;
  valueFormatter?: (params: any) => string;
  valueGetter?: (params: any) => string | null;
  valueParser?: (params: any) => string | null;
} {
  switch (typeCompo) {
    case TypeSimpleComponent.AUTO_COMPLETE:
      return {
        filter: "agTextColumnFilter",
        cellEditor: onChange
          ? buildCellEditorAutocomplete({
              tableName,
              sjmoCode,
              ctrlKey,
              componentID,
              columnIndex,
              compo,
              onChange,
            })
          : undefined,
        renderer: buildAutocompleteCellRenderer(compo, sjmoCode),
      };
    case TypeSimpleComponent.AUTO_COMPLETE_VIEW:
      return {
        filter: "agTextColumnFilter",
        cellEditor: onChange
          ? buildCellEditorAutocompleteView({
              tableName,
              sjmoCode,
              ctrlKey,
              componentID,
              columnIndex,
              compo,
              onChange,
            })
          : undefined,
        renderer: buildAutocompleteViewCellRenderer(compo, sjmoCode),
      };
    case TypeSimpleComponent.AUTO_COMPLETE_VIEW_FREE:
      return {
        filter: "agTextColumnFilter",
        cellEditor: onChange
          ? buildCellEditorAutocompleteViewFree({
              tableName,
              sjmoCode,
              ctrlKey,
              componentID,
              columnIndex,
              compo,
              onChange,
            })
          : undefined,
        renderer: buildAutocompleteViewCellRenderer(compo, sjmoCode),
      };
    case TypeSimpleComponent.SYS_DOMAINE:
      return {
        filter: "agSetColumnFilter",
        filterValues: compo.sysDomaineChoices,
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: compo.sysDomaineChoices?.map((c) => c.value),
        },
        valueFormatter: (params: any) => {
          const choice = compo.sysDomaineChoices?.find((c) => c.value === params.value);
          return choice?.label || params.value;
        },
      };
    case TypeSimpleComponent.SYS_DOMAINE_IR:
      return {
        filter: "agSetColumnFilter",
        filterValues: compo.sysDomaineChoices,
        cellStyle: {
          textAlign: "center",
        },
        renderer: buildSysDomaineIRRenderer(compo),
      };
    case TypeSimpleComponent.EDITOR:
      return {
        filter: "agTextColumnFilter",
        cellEditor: CellEditorRichText as any,
        renderer: buildHtmlRenderer(compo),
        cellEditorPopup: true,
      };
    case TypeSimpleComponent.TEXT_AREA:
      return {
        filter: "agTextColumnFilter",
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        renderer: buildHtmlRenderer(compo),
      };
    case TypeSimpleComponent.INPUT:
      return { filter: "agTextColumnFilter" };
    case TypeSimpleComponent.CHECKBOX:
      return {
        filter: "agSetColumnFilter",
        filterValues: ["true", "false"],
        cellEditor: CheckBoxCellEditor(compo),
        renderer: CheckBoxRenderer,
        filterRenderer: CheckboxFilterRenderer,
      };
    case TypeSimpleComponent.BOOLEAN:
      return {
        filter: "agSetColumnFilter",
        filterValues: [
          { value: "O", label: booleanLabels?.yes ?? "Oui" },
          { value: "N", label: booleanLabels?.no ?? "Non" },
        ],
        renderer: buildBooleanCellRenderer(compo),
      };
    case TypeSimpleComponent.CALENDAR:
      return {
        filter: "agDateColumnFilter",
        cellEditor: buildCellEditorCalendar({
          ctrlKey,
          componentID,
          columnIndex,
          compo,
          variant: "date",
        }),
        ...getCalendarColumnProps("yyyy-MM-dd", { cellDataType: "dateString" }),
      };
    case TypeSimpleComponent.CALENDAR_HOUR:
      return {
        filter: "agDateColumnFilter",
        filterParams: { includeTime: false },
        cellEditor: buildCellEditorCalendar({
          ctrlKey,
          componentID,
          columnIndex,
          compo,
          variant: "dateTime",
        }),
        ...getCalendarColumnProps("yyyy-MM-dd'T'HH:mm", {
          outputIso: true,
          cellDataType: "dateTimeString",
        }),
      };
    case TypeSimpleComponent.CALENDAR_HOUR_SECOND:
      return {
        filter: "agDateColumnFilter",
        filterParams: { includeTime: false },
        cellEditor: buildCellEditorCalendar({
          ctrlKey,
          componentID,
          columnIndex,
          compo,
          variant: "dateTimeSeconds",
        }),
        ...getCalendarColumnProps("yyyy-MM-dd'T'HH:mm:ss", {
          outputIso: true,
          cellDataType: "dateTimeString",
        }),
      };
    case TypeSimpleComponent.OEL:
      return { filter: false, renderer: buildOelRenderer(compo) };
    case TypeSimpleComponent.STATIC_HTML:
      return { filter: false, renderer: buildHtmlRenderer(compo) };
    case TypeSimpleComponent.LINK:
      return { filter: "agTextColumnFilter", renderer: buildLinkRenderer(compo) };
    case TypeSimpleComponent.INPUT_NUMBER:
      return {
        filter: "agNumberColumnFilter",
        cellEditor: "agNumberCellEditor",
        cellEditorParams: {
          min: compo.options?.min,
          max: compo.options?.max,
          showStepperButtons: true,
          ...getNumberEditorParamsFromScale(compo.scale),
        } as INumberCellEditorParams,
      };
    case TypeSimpleComponent.TAG:
      return {
        filter: "agSetColumnFilter",
        renderer: buildTagRenderer(compo),
        filterValues: compo.sysDomaineChoices,
      };
    case TypeSimpleComponent.PROCESSUS_LINK:
      return {
        filter: "agTextColumnFilter",
        renderer: buildProcessusLinkRenderer(compo, onAfterSaveProcess),
      };
    default:
      return { filter: false };
  }
}

function isEditable(
  type: "DATATABLE" | "INTERACTIVE_REPORT",
  column: ComponentState,
  isInsertable: boolean,
  isUpdatable: boolean,
) {
  if (type === "INTERACTIVE_REPORT") {
    return column.readOnly === ComponentReadOnly.DEFAULT;
  } else {
    if (!isInsertable && !isUpdatable) {
      return false;
    }
    if (
      column.typeCompo === TypeSimpleComponent.OEL ||
      column.typeCompo === TypeSimpleComponent.LINK ||
      column.typeCompo === TypeSimpleComponent.STATIC_HTML
    ) {
      return false;
    }

    return (params: any) => {
      if (params.data && params.data.id) {
        return (
          isUpdatable &&
          (column.readOnly === ComponentReadOnly.DEFAULT ||
            column.readOnly === ComponentReadOnly.READ_ONLY_INSERT) &&
          (column.disabled === ComponentReadOnly.DEFAULT ||
            column.disabled === ComponentReadOnly.READ_ONLY_INSERT)
        );
      } else {
        return (
          isInsertable &&
          (column.readOnly === ComponentReadOnly.DEFAULT ||
            column.readOnly === ComponentReadOnly.READ_ONLY_UPDATE) &&
          (column.disabled === ComponentReadOnly.DEFAULT ||
            column.disabled === ComponentReadOnly.READ_ONLY_UPDATE)
        );
      }
    };
  }
}

function getCellStyle(paramsIn: {
  column: ComponentState;
  cellStyle?: Record<string, any>;
  type: "DATATABLE" | "INTERACTIVE_REPORT";
  highlights?: HighlightProperties[];
}) {
  return (params: any) => {
    const { column, cellStyle, type, highlights } = paramsIn;
    if (highlights && highlights.length > 0) {
      const filteredHighlights = highlights
        .filter((h) => h.isActive && h.typeHighlight === "column" && h.column === column.column)
        .sort((a, b) => a.sequence - b.sequence);

      for (const highlight of filteredHighlights) {
        if (isHighlighted(column.typeCompo, params.data, highlight)) {
          const style: Record<string, string> = {};
          if (highlight.backgroundColor) style.backgroundColor = highlight.backgroundColor;
          if (highlight.color) style.color = highlight.color;
          return style;
        }
      }
    }

    // Autres styles (OEL, styleContextuel, etc.)
    if (
      type === "DATATABLE" &&
      params.data &&
      column.oel &&
      column.typeCompo !== TypeSimpleComponent.OEL &&
      params.data._style
    ) {
      return {
        ...cellStyle,
        ...params.data._style[column.column],
        textAlign: column.typeCompo === TypeSimpleComponent.INPUT_NUMBER ? "right" : undefined,
      };
    } else if (column.typeCompo === TypeSimpleComponent.INPUT_NUMBER) {
      return { ...cellStyle, textAlign: "right" };
    }

    if (type === "INTERACTIVE_REPORT" && params.data && column.options?.styleContextuel) {
      return { ...cellStyle, ...column.options.styleContextuel[column.column][params.value] };
    }
    return {};
  };
}

function getCellClasssesByWvi(wviType: string | undefined) {
  switch (wviType) {
    case "danger":
      // eslint-disable-next-line
      return tw`border border-red-500`;
    case "warning":
      // eslint-disable-next-line
      return tw`border border-yellow-500`;
    case "success":
      // eslint-disable-next-line
      return tw`border border-green-500`;
    default:
      return undefined;
  }
}

export function flatDataFromPopjo(pojo: Partial<Pojo>): Partial<Pojo> {
  return Object.entries(pojo).reduce((acc, [key, value]) => {
    if (value instanceof Date) {
      acc[key] = value.toISOString();
    } else if (value && typeof value === "object") {
      acc[key] = (value as any).id;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
}

const gridStyle = { height: "100%", width: "100%", padding: "0" };

const NoRowsOverlay: FC<{ message: string }> = ({ message }) => (
  <div className={tw("flex items-center gap-2 text-gray-400 text-sm")}>
    <Fa icon="inbox" />
    <span>{message}</span>
  </div>
);

function getFocusSortValues(
  currentFocus: DatatableFocusState | undefined,
  definitionType: "DATATABLE" | "INTERACTIVE_REPORT",
): Record<string, DatatableSort> {
  if (!currentFocus) {
    return {};
  }
  if (definitionType === "DATATABLE") {
    return currentFocus.sort ? parseSortStr(currentFocus.sort) : {};
  }
  return (currentFocus.currentOrderBy ?? []).reduce(
    (acc, { colId, sort }) => {
      if (colId && sort) {
        acc[colId] = sort === "desc" ? "DESC" : "ASC";
      }
      return acc;
    },
    {} as Record<string, DatatableSort>,
  );
}

function getVisibleColumnIds(columns: ComponentState[]): Set<string> {
  return new Set(
    columns
      .filter((column) => column.compoVisible || column.hiddenIsFiltered !== true)
      .map((column) => column.column),
  );
}

function toAgGridSortItems(
  sortValues: Record<string, DatatableSort>,
  visibleColIds: Set<string>,
): ColumnState[] {
  // sortIndex est obligatoire : sans lui, AG Grid numérote les tris
  // selon l'ordre visuel des colonnes (gauche → droite), pas selon ce tableau.
  return Object.entries(sortValues)
    .filter(([colId]) => visibleColIds.has(colId))
    .map(([colId, sort], sortIndex) => ({
      colId,
      sort: sort === "DESC" ? "desc" : "asc",
      sortIndex,
    }));
}

function calculateSortValues(
  previousSort: Record<string, DatatableSort>,
  visibleColIds: Set<string>,
  agGridSortState: {
    colId: string;
    sort: "asc" | "desc" | null | undefined;
    sortIndex: number | null | undefined;
  }[],
): Record<string, DatatableSort> {
  const toRemove = agGridSortState.filter((column) => !column.sort && column.colId);
  const toAdd = agGridSortState.filter((column) => column.sort && column.colId);

  if (toRemove.length > 0 && toAdd.length === 1) {
    // Cas particulier, si on reçoit sur un même event du remove et du add, c'est qu'on est dans le cas où l'utilisateur à cliquer sans "shift" donc il replace tous le tris par un nouveau.
    // Il faut donc supprimer tous l'existant y compris les colonnes cachées.
    return Object.fromEntries(
      toAdd.map((column) => [column.colId, column.sort === "desc" ? "DESC" : "ASC"]),
    );
  }

  const newSort = Object.entries(previousSort).filter(
    ([colId]) => !toRemove.some((column) => column.colId === colId),
  );
  newSort.push(
    ...toAdd.map(
      (column) =>
        [column.colId, column.sort === "desc" ? "DESC" : "ASC"] as [string, DatatableSort],
    ),
  );
  return Object.fromEntries(newSort);
}

function sortValuesToSortModelItems(sortValues: Record<string, DatatableSort>): SortModelItem[] {
  return Object.entries(sortValues).map(([colId, sort]) => ({
    colId,
    sort: sort === "DESC" ? "desc" : "asc",
  }));
}

const AgGridContainer = forwardRef<AgGridReact, DatatableAGGridProps>(
  (
    {
      sjmoCode,
      tableName,
      processOrigine,
      ctrlkey,
      sjpaId,
      additionnalClause,
      definition,
      currentFocus,
      parentHighlights,
      dirtyRows,
      keepDirty,
      insertable,
      wviState,
      clearCacheOnClose,
      initialState,
      triggerSave,
      triggerAdd,
      addIntent,
      triggerRefresh,
      hideFilterBar,
      groupByState,
      defaultOpenBreakRows = false,
      pinnedState,
      onChangeParent,
      setKeepDirty,
      saveRows,
      deleteRows,
      onAfterSaveDatatable,
      onAfterDeleteDatatable,
      getRSQLFilter,
      fetchData,
      onSaveFocusPerso,
      onRazFocusPerso,
      onFocusChange,
      onColumnMoved,
      onColumnVisible,
      onRowClickParent,
      clearDirtyRows,
      onColumnResized,
      onCellKeyDown,
      onAdd,
      setSelectedRowsParent,
      getRowId,
      onChangeAggregates,
      onChangeGroupBy,
      onChangeDefaultOpenBreakRows,
      onChangePinned,
    },
    ref,
  ) => {
    const gridRef = ref as MutableRefObject<AgGridReact<any> | null>;
    const { t } = useTranslation();
    const interactions = useInteractions({ sjmoCode, ctrlKey: ctrlkey, tableName });
    const lang = sessionStorage.getItem("userLang") || "fr";
    const reduxDispatch = useDispatch();

    // État local pour la mise en valeur manuelle
    const [highlights, setHighlights] = useState<HighlightProperties[]>([]);
    const effectiveHighlights = useMemo(() => {
      const all = [...(highlights || [])];
      return all;
    }, [definition.type, highlights]);

    const onValidateHighlights = useCallback((highlight: HighlightProperties) => {
      setHighlights((prev) => {
        const index = prev.findIndex((h) => h.id === highlight.id);
        const next = index !== -1 ? [...prev] : [...prev, highlight];
        if (index !== -1) next[index] = highlight;
        return next.sort((a, b) => b.sequence - a.sequence);
      });
    }, []);

    const onDeleteHighlights = useCallback((index: number) => {
      setHighlights((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const onClearHighlights = useCallback(() => {
      setHighlights([]);
    }, []);

    const oelColumns = useMemo(() => {
      return definition.columns
        .filter((c) => c.typeCompo === TypeSimpleComponent.OEL && c.compoVisible)
        .map((c) => c.column);
    }, [definition.columns]);

    const [selectedRows, setSelectedRows] = useState<Pojo[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    // Déclencheur de rafraîchissement de rowClassRules après un clic : lastFocusedRowIdRef
    // (ref) reste l'unique source de la ligne courante, mais une ref ne provoque pas de
    // re-render ; ce compteur force AG Grid à réévaluer la règle de surlignage.
    const [rowClassRefreshTick, bumpRowClassRefresh] = useState(0);
    const [filterBar, setFilterBar] = useState<FilterBar | undefined>(initialState?.filterBar);
    const [filterAllFields, setFilterAllFields] = useState<string | undefined>(
      initialState?.filterAllFields ?? undefined,
    );
    const [
      { currentFilter, rowCount, columnFilter, currentLocalFilter, currentLocalOrderBy },
      setCurrentFilter,
    ] = useState<{
      currentFilter: string | undefined;
      rowCount: number;
      columnFilter: string[];
      currentLocalFilter: FilterModel | undefined;
      currentLocalOrderBy: SortModelItem[] | undefined;
    }>({
      currentFilter: undefined,
      rowCount: 0,
      columnFilter: [],
      currentLocalFilter: undefined,
      currentLocalOrderBy: undefined,
    });
    const [existSatellite, setExistSatellite] = useState<Record<string, boolean>>({});

    const [gridPadding, setGridPadding] = useState<"small" | "large">("large");
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState<Pojo[] | undefined>(undefined);
    const aggregates = useMemo(() => currentFocus?.aggregates ?? [], [currentFocus?.aggregates]);

    const hasDirtyRows = useMemo(
      () => !!(dirtyRows && Object.keys(dirtyRows).length > 0),
      [dirtyRows],
    );

    const { flatSelectedRows, selectedNumber } = useMemo(() => {
      const flatSelectedRows = selectedRows.map((r) => {
        let flat = {};
        if (dirtyRows && dirtyRows[r.id]) {
          flat = { ...flatDataFromPopjo(r), ...dirtyRows[r.id] };
        } else {
          flat = flatDataFromPopjo(r);
        }
        return flat as Pojo;
      });
      const selectedNumber = flatSelectedRows.length;
      return { flatSelectedRows, selectedNumber };
    }, [dirtyRows, selectedRows]);

    const [showConfirmModal, setShowConfirmModal] = useState<ShowConfirmModalProps | null>(null);

    const { registerHeight, maxHeight: hookMaxHeight } = useRowMaxHeight();
    const maxRowHeight = Math.max(hookMaxHeight, MIN_ROW_HEIGHT);

    const onSelectionChanged = useCallback(
      (event: SelectionChangedEvent) => {
        setSelectedRows(event.api.getSelectedNodes().map((node) => node.data));
        setSelectedRowsParent &&
          setSelectedRowsParent(event.api.getSelectedNodes().map((node) => node.data));
      },
      [setSelectedRowsParent],
    );

    const setSelectAllWithParent = useCallback(
      (selectAll: boolean) => {
        setSelectAll(selectAll);
        if (selectAll && gridRef.current?.api) {
          const allRows: any[] = [];
          gridRef.current.api.forEachNode((node: any) => {
            if (node.data) {
              allRows.push(node.data);
            }
          });
          setSelectedRowsParent && setSelectedRowsParent(allRows);
        } else if (!selectAll && gridRef.current?.api) {
          setSelectedRowsParent && setSelectedRowsParent([]);
          setSelectedRows([]);
          gridRef.current?.api.deselectAll();
        }
      },
      [gridRef, setSelectedRowsParent, setSelectedRows],
    );

    const localeText = useMemo(() => {
      switch (lang) {
        case "en":
          return AG_GRID_LOCALE_EN;
        case "de":
          return AG_GRID_LOCALE_DE;
        case "it":
          return AG_GRID_LOCALE_IT;
        default:
          return AG_GRID_LOCALE_FR;
      }
    }, [lang]);

    const oldFocusIdRef = useRef<string | undefined>(undefined);
    const effectiveSortRef = useRef<Record<string, DatatableSort>>({});
    useEffect(() => {
      // Récupéation et gestion du type du filtre privilégié à l'initialisation uniquement
      // On ne change jamais le filtre courant lors d'un changement de focus
      const filterBarPrivilegie = currentFocus?.filters.find(
        (f) => f.privilegie,
      ) as any as FilterBar | null;
      if (filterBarPrivilegie && filterBar === undefined) {
        setFilterBar({
          filterBarId: filterBarPrivilegie.filterBarId,
          startDate: filterBarPrivilegie.startDate,
          endDate: filterBarPrivilegie.endDate,
        });
      }

      setFilterAllFields(undefined);
      setHighlights(currentFocus?.highlights ?? []);
      onChangePinned?.(currentFocus?.currentPin ?? []);

      // Pour la datatable on ne remplace les tris et les filtres existant par ceux du focus que dans le cas où il y en a de définis
      // Pour les autres composants (IR) on remplace toujours

      // On récupère les tris existants dans l'aggrid avant le changement de focus
      let visibleFocusOrderBy: ColumnState[] = (gridRef.current?.api?.getColumnState() ?? [])
        .filter((col) => col.sort != null)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map(({ colId, sort }, sortIndex) => ({
          colId,
          sort: sort ?? "asc",
          sortIndex,
        }));

      if (definition.type !== "DATATABLE" || currentFocus?.sort) {
        const focusSort = getFocusSortValues(currentFocus, definition.type);
        effectiveSortRef.current = focusSort;
        const visibleColIds = getVisibleColumnIds(definition.columns);
        visibleFocusOrderBy = toAgGridSortItems(focusSort, visibleColIds);
      }

      if (gridRef.current?.api) {
        if (definition.type !== "DATATABLE" || currentFocus?.currentFilter) {
          gridRef.current.api.setFilterModel(currentFocus?.currentFilter ?? null);
        }
        gridRef.current.api.applyColumnState({
          state: withSelectionColumnPinned([
            ...visibleFocusOrderBy,
            ...(currentFocus?.currentPin ?? []),
          ]),
          defaultState: { sort: null, sortIndex: null, pinned: null },
        });
      }

      oldFocusIdRef.current = currentFocus?.focusId;
      // On ne reset les filtres etc que lorsque le focusId change, il ne faut rien changer quand une colonne est resize ou déplacée par exemple
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFocus?.focusId, gridRef.current?.api !== undefined]);

    const onChange = useCallback(
      (params: ValueSetterParams<Pojo, string>) => {
        onChangeParent &&
          onChangeParent(params, isWaitingForSaveRef.current, isStoppingEditForAddRef.current);
      },
      [onChangeParent],
    );

    // Colonnes visibles du focus au moment de son chargement.
    // Le décochage dans la session ne retire pas la colonne du sélecteur.
    const focusChooserColumnsRef = useRef<{ focusId?: string; visibleIds: Set<string> }>({
      visibleIds: new Set(),
    });
    if (
      definition.type === "DATATABLE" &&
      currentFocus?.focusId &&
      definition.columns.length > 0 &&
      focusChooserColumnsRef.current.focusId !== currentFocus.focusId
    ) {
      focusChooserColumnsRef.current = {
        focusId: currentFocus.focusId,
        visibleIds: new Set(
          definition.columns.filter((column) => column.compoVisible).map((column) => column.column),
        ),
      };
    }

    const columnDefs = useMemo(() => {
      const columns: ColDef[] = [];

      if (definition.type === "DATATABLE") {
        columns.push({
          field: "satellite",
          headerName: "",
          sortable: false,
          filter: false,
          suppressHeaderContextMenu: true,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true,
          width: 50,
          lockVisible: true,
          suppressColumnsToolPanel: true,
          contextMenuItems: [],
          pinned: "left",
          cellRenderer: SateliteMenuRenderer({
            sjmoCode,
            ctrlkey,
            currentFilter,
            columns: definition.columns,
            existSatellite,
          }),
        });
      }

      columns.push(
        ...definition.columns
          .filter((c) => c.compoVisible || c.hiddenIsFiltered !== true)
          .map((column) => {
            const {
              filter,
              filterParams: typeFilterParams,
              filterValues,
              renderer,
              filterRenderer,
              cellEditor,
              cellEditorPopup,
              cellEditorParams,
              cellStyle,
              cellDataType,
              outputIso,
              valueFormatter,
              valueGetter,
              valueParser,
            } = getPropsByTypeCompo({
              typeCompo: column.typeCompo,
              tableName,
              sjmoCode,
              ctrlKey: ctrlkey,
              componentID: column.id,
              columnIndex: definition.columns.indexOf(column),
              compo: column,
              onChange: onChangeParent ? onChange : undefined,
              onAfterSaveProcess: () => {
                gridRef?.current?.api?.setGridOption("serverSideDatasource", { getRows: getRows });
              },
              booleanLabels: { yes: t("commun_oui"), no: t("commun_non") },
            });

            if (column.column === "drvResteALivrerUv") {
              console.log(
                "column",
                column,
                currentFocus?.insertable ?? false,
                currentFocus?.updatatable ?? false,
                isEditable(
                  definition.type,
                  column,
                  currentFocus?.insertable ?? false,
                  currentFocus?.updatatable ?? false,
                ),
              );
            }

            const allGroupColumns = [...(groupByState?.columns ?? [])];

            const isColumnSortable = column.typeCompo !== TypeSimpleComponent.OEL;
            return {
              field: column.column,
              headerName: column.label,
              sortable: isColumnSortable,
              resizable: true,
              editable: onChangeParent
                ? isEditable(
                    definition.type,
                    column,
                    currentFocus?.insertable ?? false,
                    currentFocus?.updatatable ?? false,
                  )
                : false,
              cellRenderer: renderer,
              cellEditor: onChangeParent ? cellEditor : undefined,
              cellEditorParams: onChangeParent ? cellEditorParams : undefined,
              cellEditorPopup: onChangeParent ? cellEditorPopup : undefined,
              width: Math.max(column.contentSize, column.labelSize),
              headerTooltip: column.tooltip,
              headerStyle: column.mandatory ? { textDecoration: "underline red" } : undefined,
              headerClass: hasDirtyRows && isColumnSortable ? "ru-header-sort-locked" : undefined,
              filter: filter,
              filterParams: {
                ...typeFilterParams,
                values: filterValues,
                keyCreator:
                  filterValues && filterValues.length > 0 && filterValues[0] instanceof Object
                    ? getValueFromSd
                    : undefined,
                valueFormatter:
                  filterValues && filterValues.length > 0 && filterValues[0] instanceof Object
                    ? getLabelFromSd
                    : undefined,
                cellRenderer: filterRenderer,
                buttons: ["apply", "reset"],
                readOnly: hasDirtyRows ? true : !isColumnSortable,
              },
              suppressHeaderFilterButton: hasDirtyRows ? true : !isColumnSortable,
              suppressHeaderKeyboardEvent: hasDirtyRows ? () => true : undefined,
              rowGroup: allGroupColumns.includes(column.column),
              hide: !column.compoVisible || allGroupColumns.includes(column.column),
              suppressColumnsToolPanel:
                definition.type === "DATATABLE"
                  ? !focusChooserColumnsRef.current.visibleIds.has(column.column)
                  : undefined,
              cellDataType,
              outputIso,
              cellStyle: getCellStyle({
                column,
                type: definition.type,
                highlights: effectiveHighlights,
              }),
              cellClass: (params: CellClassParams) => {
                const wviType = wviState?.[params.rowIndex]?.[column.column]?.type?.toLowerCase();
                return getCellClasssesByWvi(wviType);
              },
              valueFormatter,
              valueGetter,
              valueParser,
              context: {
                typeCompo: column.typeCompo,
                compo: column,
              },
              valueSetter: onChange,
              // Enter est géré par HeadlessUI (sélection dropdown) + onEnterSelect (commit).
              // Sans cette suppression, AG-Grid appelle stopEditing avant HeadlessUI,
              // ce qui provoque un blur → findOne(terme partiel) → mauvaise valeur commitée.
              suppressKeyboardEvent: [
                TypeSimpleComponent.AUTO_COMPLETE,
                TypeSimpleComponent.AUTO_COMPLETE_VIEW,
                TypeSimpleComponent.AUTO_COMPLETE_VIEW_FREE,
                TypeSimpleComponent.EDITOR,
                TypeSimpleComponent.TEXT_AREA,
              ].includes(column.typeCompo)
                ? (params: SuppressKeyboardEventParams) =>
                    params.editing && "Enter" == params.event.key
                : undefined,
            } as unknown as ColDef;
          }),
      );
      return columns;
      // On désactive le exhaustive-deps car on ne veut pas que le gridRef et getRows soit dans les dépendances
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      sjmoCode,
      ctrlkey,
      currentFilter,
      definition.columns,
      definition.type,
      existSatellite,
      tableName,
      onChangeParent,
      groupByState,
      wviState,
      effectiveHighlights,
      gridRef,
      hasDirtyRows,
      t,
    ]);

    const allColumnRef = useMemo(() => {
      return definition.columns.reduce(
        (acc, column) => {
          acc[column.column] = column;
          return acc;
        },
        {} as Record<string, ComponentState>,
      );
    }, [definition.columns]);

    const processCellForClipboard = useCallback((params: ProcessCellForExportParams) => {
      const colDef = params.column.getColDef();
      const typeCompo = colDef.context?.typeCompo as string | undefined;
      const compo = colDef.context?.compo as ComponentState | undefined;
      return formatCellValueForClipboard(params.value, typeCompo, params.formatValue, compo);
    }, []);

    const autoGroupColumnDef = useMemo<ColDef>(() => {
      const groupColumns = groupByState?.columns ?? [];

      return {
        minWidth: 200,
        headerValueGetter: (params) =>
          groupColumns.length > 0
            ? groupColumns.map((field) => allColumnRef[field]?.label ?? field).join(" / ")
            : (params.colDef.headerName ?? ""),
        headerClass: hasDirtyRows ? "ru-header-sort-locked" : undefined,
        suppressHeaderFilterButton: !!hasDirtyRows,
        suppressHeaderKeyboardEvent: hasDirtyRows ? () => true : undefined,
      };
    }, [groupByState, allColumnRef, hasDirtyRows]);

    const isServerSideGroupOpenByDefault = useCallback(() => {
      const groupColumns = groupByState?.columns ?? [];
      if (groupColumns.length === 0) {
        return false;
      }
      return defaultOpenBreakRows;
    }, [groupByState?.columns, defaultOpenBreakRows]);

    // id de la dernière ligne ayant reçu le focus : permet de re-contextualiser dessus
    // après un refresh / changement de tri plutôt que sur la première ligne reçue.
    const lastFocusedRowIdRef = useRef<Pojo["id"] | null>(null);
    // position de scroll vertical courante (px), suivie en continu via onBodyScroll.
    const scrollTopRef = useRef(0);
    // position de scroll à restaurer une fois les données rechargées (refresh / tri).
    const pendingScrollRestoreRef = useRef<number | null>(null);
    // wrapper de la grille, utilisé pour retrouver le viewport et restaurer le scroll.
    const gridWrapperRef = useRef<HTMLDivElement | null>(null);
    // wrapper de la toolbar : sa hauteur réelle varie (ex: scrollbar horizontale qui
    // apparaît quand le panel est trop étroit pour son contenu), on ne peut pas la
    // supposer fixe pour calculer la hauteur restante allouée à la grille.
    const toolbarWrapperRef = useRef<HTMLDivElement | null>(null);
    const [toolbarHeight, setToolbarHeight] = useState(DEFAULT_TOOLBAR_HEIGHT);

    useEffect(() => {
      const element = toolbarWrapperRef.current;
      if (!element) {
        return;
      }
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setToolbarHeight(entry.contentRect.height);
        }
      });
      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    const getRows = useCallback(
      (params: IServerSideGetRowsParams) => {
        params.api.hideOverlay();
        const effectiveGroupColumns = groupByState?.columns ?? [];

        if (!clearCacheOnClose) {
          const key = getGridDtKey(sjmoCode, ctrlkey, tableName);
          GRID_DT_CACHE.get(key).then((state) => {
            if (state) {
              const newState: GridDtCacheState = {
                ...state,
                searchParams: params.request,
                breakRows: effectiveGroupColumns,
                filterBar: filterBar,
                filterAllFields: filterAllFields,
              };

              GRID_DT_CACHE.set(key, newState);
            }
          });
        }
        const localSortModel = effectiveSortRef.current;

        const rsql = getRSQLFilter(
          interactions,
          params.request.filterModel as AGFilter,
          Object.keys(localSortModel).length > 0 ? localSortModel : {},
          effectiveGroupColumns,
          additionnalClause,
          allColumnRef,
        );

        // Dans le cas où il y a un aggregat il faut faire un filtre qui va ignorer un éventuel group By
        // C'est donc le même mais on force breakrows et l'order by à vide
        let aggregateFilter: RSQLCriteria | undefined = undefined;
        if (aggregates && aggregates.length > 0) {
          aggregateFilter = getRSQLFilter(
            interactions,
            params.request.filterModel as AGFilter,
            {},
            [],
            additionnalClause,
            allColumnRef,
          );
        }

        const rowGroupCols = params.request.rowGroupCols ?? [];
        const groupKeys = params.request.groupKeys ?? [];

        if (filterAllFields && filterAllFields.length > 0) {
          const filtered = getFilterAllField(definition, filterAllFields);
          rsql.filters.and(filtered);
        }

        // Ajout des critères de filtre pour le groupement
        if (rowGroupCols.length > 0 && groupKeys.length > 0) {
          const filtered = getGroupByFilter(
            rowGroupCols,
            groupKeys,
            rsql,
            params.request.groupKeys ?? [],
          );
          if (filtered) {
            rsql.filters.and(filtered);
          }
        }

        const groupByStr =
          rowGroupCols.length > 0 && rowGroupCols.length > groupKeys.length
            ? rowGroupCols[groupKeys.length].field
            : undefined;

        fetchData({
          sjmoCode,
          tableName,
          ctrlkey,
          currentFocus,
          startIndex: params.request.startRow ?? 0,
          stopIndex: params.request.endRow ?? MIN_BATCH_SIZE,
          reset: false,
          cancelToken: Axios.CancelToken.source(),
          oelColumns,
          query: rsql,
          filterBar,
          groupBy: groupByStr,
          groupByFunctions: groupByState?.functionList ?? [],
          aggregates,
          aggregateFilter,
        })
          .then((res) => {
            // On ne garde que les colonnes présentes dans rowGroupCols si spécifié
            let result: Record<string, any>[] = res.data;
            if (
              params.request.rowGroupCols?.length > 0 &&
              params.request.rowGroupCols.length !== params.request.groupKeys.length
            ) {
              result = res.data.map((row) => {
                const groupedRow: Record<string, any> = { ...row };
                params.request.rowGroupCols.forEach((col) => {
                  groupedRow[col.field as string] = row[col.field as string] ?? "";
                });

                // il faut prendre l'idnex en fonction des groupes ouverts
                const index =
                  params.request.groupKeys && params.request.groupKeys.length > 0
                    ? params.request.groupKeys.length
                    : 0;

                // On préfixe avec les clés des groupes parents déjà ouverts (groupKeys),
                // puis on ajoute la valeur du niveau de groupement courant lue depuis la row.
                const parentGroupKeys = params.request.groupKeys
                  ? params.request.groupKeys.map((groupValue: any) => {
                      return typeof groupValue === "object" ? `${groupValue.id}` : `${groupValue}`;
                    })
                  : [];

                const currentLevelCol = params.request.rowGroupCols[index];
                const currentValue = currentLevelCol
                  ? (row[currentLevelCol.field as string] ?? "")
                  : "";
                const currentIdValue =
                  typeof currentValue === "object" ? `${currentValue.id}` : `${currentValue}`;

                const idValues = [...parentGroupKeys, currentIdValue];
                groupedRow["id"] = idValues.join("_");
                groupedRow["uuid"] = uuidv4();
                groupByState?.functionList.forEach((fn) => {
                  const label = t("commun_aggrate_function_" + fn.functionType.toLowerCase());
                  groupedRow[fn.column] = `${label} : ${row[fn.label] ?? ""}`;
                });
                return groupedRow;
              });

              // Filtrer pour n'avoir que des IDs uniques
              result = result.filter(
                (row, index, self) => index === self.findIndex((r) => r.id === row.id),
              );
            }

            // Fusionner les lignes modifiées avec le résultat
            const dirtyRowsCount =
              dirtyRows && Object.keys(dirtyRows).length > 0 ? Object.keys(dirtyRows).length : 0;
            // On compte le nombre de nouvelles lignes en dirty non créer en base
            const newsRowsCount = Object.values(dirtyRows ?? {}).filter(
              (row) => row.id === undefined || row.id === null,
            ).length;

            if (dirtyRowsCount > 0) {
              // On enrichit "result" avec dirtyRows selon la position pour les rows sans id,
              // et on remplace selon l'id pour celles qui ont un id.

              // dirtyRows est de la forme { position: row }
              const dirtyRowsMap = dirtyRows ?? {};

              // Copie de result pour la manip; on fera les insert/replacements sur cette copie
              let enrichedResult = [...result];

              // Ajouter/Remplacer lignes selon dirtyRows
              Object.entries(dirtyRowsMap).forEach(([positionStr, dirtyRow]) => {
                const position = parseInt(positionStr, 10);

                if (dirtyRow.id === undefined || dirtyRow.id === null) {
                  // Si la row n'a pas d'id, on l'insère à la position demandée
                  if (Number.isFinite(position) && position >= 0) {
                    enrichedResult.splice(position, 0, dirtyRow);
                  } else {
                    // Si la position n'est pas valide on ajoute à la fin
                    enrichedResult.push(dirtyRow);
                  }
                } else {
                  // Si la row a un id, on cherche son index dans result
                  const indexInResult = enrichedResult.findIndex((r) => r && r.id === dirtyRow.id);
                  if (indexInResult !== -1) {
                    enrichedResult[indexInResult] = dirtyRow;
                  } else {
                    // Si cet id n'est pas trouvé, on ajoute à la fin
                    enrichedResult.push(dirtyRow);
                  }
                }
              });

              result = enrichedResult;
            }

            params.success({
              rowData: result,
              rowCount: res.meta.totalRecords + newsRowsCount,
            });

            // Restauration de la position de scroll après un refresh / changement de tri.
            if (pendingScrollRestoreRef.current != null) {
              const scrollToRestore = pendingScrollRestoreRef.current;
              pendingScrollRestoreRef.current = null;
              requestAnimationFrame(() => {
                const viewport =
                  gridWrapperRef.current?.querySelector<HTMLElement>(".ag-body-viewport");
                if (viewport) {
                  viewport.scrollTop = scrollToRestore;
                }
              });
            }

            if (result.length === 0) {
              params.api.showNoRowsOverlay();
            } else {
              params.api.hideOverlay();
            }

            existList({ tableName, ids: res.data.map((d) => d.id) }).then(
              (satelliteExistResponse) => {
                setExistSatellite((x) => ({ ...x, ...satelliteExistResponse.data }));
              },
            );

            const columnFilter = explainFilter(
              params.request.filterModel as AGFilter,
              allColumnRef,
              localeText,
              { yes: t("commun_oui"), no: t("commun_non") },
            );

            setCurrentFilter({
              currentFilter: rsql.build(),
              rowCount: res.meta.totalRecordsWithoutGrouping ?? res.meta.totalRecords,
              columnFilter: columnFilter,
              currentLocalFilter: params.request.filterModel as FilterModel,
              currentLocalOrderBy: sortValuesToSortModelItems(localSortModel),
            });

            // maj interaction en tant que parent sur la dernière ligne ayant reçu le focus.
            // À défaut (premier chargement, aucun focus mémorisé), on contextualise sur la
            // première ligne reçue. Si la ligne focus n'est pas présente dans le bloc reçu,
            // on ne recontextualise pas pour ne pas écraser la sélection courante.
            if (!onRowClickParent) {
              const lastFocusedId = lastFocusedRowIdRef.current;
              const focusedRow =
                lastFocusedId != null
                  ? res.data.find((row) => row.id === lastFocusedId || row.uuid === lastFocusedId)
                  : undefined;

              if (focusedRow) {
                // Si l'ancienne ligne sélectionnée est toujours présente, on recontextualise à partir de cette ligne
                reduxDispatch(
                  contextualize(
                    sjmoCode,
                    tableName,
                    ctrlkey,
                    flatDataFromPopjo(focusedRow) as Pojo,
                  ),
                );
              } else if (res.data.length > 0) {
                // Si ( l'ancienne ligne sélectionnée n'est plus présente OU n'existe pas) et qu'il y a des données, on contextualise sur la première ligne
                reduxDispatch(
                  contextualize(
                    sjmoCode,
                    tableName,
                    ctrlkey,
                    flatDataFromPopjo(res.data[0]) as Pojo,
                  ),
                );
              } else if (lastFocusedId != null && res.data.length === 0) {
                // Si il existait une sélection et qu'il n'y a plus de données, on décontextualise
                reduxDispatch(contextualize(sjmoCode, tableName, ctrlkey, null));
              }
              // Sinon, le cas restant, pas d'ancienne selection et pas de données, on ne fait rien
            }

            if (!res.meta.aggregates || Object.keys(res.meta.aggregates).length === 0) {
              setPinnedBottomRowData(undefined);
            } else {
              const aggregatesRecord: Record<string, any> = {};
              res.meta.aggregates &&
                aggregates.forEach((aggr) => {
                  const value = res.meta.aggregates?.[aggr.id];
                  if (value !== undefined && value !== null) {
                    const label = t("commun_aggrate_function_" + aggr.functionType.toLowerCase());
                    aggregatesRecord[aggr.column] = `${label} : ${value}`;
                  }
                });
              setPinnedBottomRowData([aggregatesRecord as Pojo]);
            }

            // Données effectivement rafraîchies : on résout le deferred éventuellement
            // en attente pour permettre à onSave / onDelete de se résoudre maintenant.
            if (refreshDeferredRef.current) {
              refreshDeferredRef.current.resolve();
              refreshDeferredRef.current = null;
            }
          })
          .catch((error) => {
            console.error("error during the getRows", error);
            params.fail();
            // On résout aussi en cas d'échec pour ne pas bloquer indéfiniment le bouton.
            if (refreshDeferredRef.current) {
              refreshDeferredRef.current.resolve();
              refreshDeferredRef.current = null;
            }
          });
      },
      [
        clearCacheOnClose,
        currentFocus,
        getRSQLFilter,
        interactions,
        groupByState,
        additionnalClause,
        allColumnRef,
        filterAllFields,
        fetchData,
        sjmoCode,
        tableName,
        ctrlkey,
        oelColumns,
        filterBar,
        definition,
        dirtyRows,
        localeText,
        onRowClickParent,
        reduxDispatch,
        gridRef,
        aggregates,
        setPinnedBottomRowData,
      ],
    );

    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        const visibleColIds = getVisibleColumnIds(definition.columns);
        if (initialState?.searchParams) {
          params.api.setFilterModel(initialState.searchParams.filterModel);
          params.api.applyColumnState({
            state: (initialState.searchParams.sortModel ?? []).map((item, sortIndex) => ({
              ...item,
              sortIndex,
            })),
            defaultState: { sort: null, sortIndex: null },
          });
          effectiveSortRef.current = (initialState.searchParams.sortModel ?? []).reduce(
            (acc, { colId, sort }) => {
              if (colId && sort) {
                acc[colId] = sort === "asc" ? "ASC" : "DESC";
              }
              return acc;
            },
            {} as Record<string, DatatableSort>,
          );
        } else {
          const focusSort = getFocusSortValues(currentFocus, definition.type);
          effectiveSortRef.current = focusSort;
          if (currentFocus?.currentFilter) {
            params.api.setFilterModel(currentFocus?.currentFilter);
          }
          const visibleFocusOrderBy = toAgGridSortItems(focusSort, visibleColIds);
          if (visibleFocusOrderBy.length > 0 || currentFocus?.currentPin) {
            params.api.applyColumnState({
              state: withSelectionColumnPinned([
                ...visibleFocusOrderBy,
                ...(currentFocus?.currentPin ?? []),
              ]),
              defaultState: { sort: null, sortIndex: null, pinned: null },
            });
          }
        }
        params.api.setGridOption("serverSideDatasource", { getRows: getRows });
      },
      [currentFocus, definition.columns, definition.type, getRows, initialState?.searchParams],
    );

    // refresh lors du changement de filtre, on utilise un useeffect car il faut lancer le refresh après avoir mis à jours le state
    const refreshData = useCallback(
      (keepDirtyLocal?: boolean) => {
        // on mémorise la position de scroll courante pour la restaurer après le rechargement
        pendingScrollRestoreRef.current = scrollTopRef.current;

        if (keepDirtyLocal === false && clearDirtyRows) {
          clearDirtyRows();
          return;
        } else if (keepDirtyLocal === true) {
          gridRef?.current?.api?.setGridOption("serverSideDatasource", { getRows: getRows });
          return;
        }

        if (
          !keepDirty &&
          clearDirtyRows &&
          ((dirtyRows && Object.keys(dirtyRows).length > 0) ||
            (wviState && Object.keys(wviState).length > 0))
        ) {
          clearDirtyRows && clearDirtyRows();
        } else {
          gridRef?.current?.api?.setGridOption("serverSideDatasource", { getRows: getRows });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [gridRef, getRows, keepDirty, clearDirtyRows, interactions, dirtyRows, wviState],
    );

    useEffect(() => {
      refreshData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerRefresh]);

    const oldInteractions = useRef(interactions);
    // refresh qui suprime le dirty
    useEffect(() => {
      const isLoadingInitialInteractions = Object.keys(oldInteractions.current).length === 0;
      refreshData(isLoadingInitialInteractions);
      oldInteractions.current = interactions;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interactions, keepDirty]);

    // refresh qui conserve le dirty
    useEffect(() => {
      refreshData(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      JSON.stringify(groupByState),
      defaultOpenBreakRows,
      additionnalClause,
      filterBar,
      filterAllFields,
      aggregates,
      getRSQLFilter,
    ]);

    // isapiPendingRef : sert à éviter le double clic
    const isApiPendingRef = useRef(false);
    // waitForSaveRef : sert à gérer la synchronisation entre onchange, wvi et save
    const isWaitingForSaveRef = useRef<boolean>(false);
    // isStoppingEditForAddRef : sert à gérer la synchronisation entre onchange, wvi et ajout
    const isStoppingEditForAddRef = useRef<boolean>(false);
    const pendingAddRef = useRef<{
      position: "START" | "END" | number;
      initialValue?: Record<string, any>;
    } | null>(null);

    // refreshDeferredRef : permet d'attendre la fin effective du refresh des données
    // (le refresh est piloté par AG Grid via getRows, donc asynchrone).
    // Il est résolu dans le callback de getRows (succès ou échec) pour que onSave / onDelete
    // puissent retourner une promesse qui ne se résout qu'une fois les données réellement rafraîchies.
    const refreshDeferredRef = useRef<{
      resolve: () => void;
      promise: Promise<void>;
    } | null>(null);

    const createRefreshDeferred = useCallback(() => {
      // Si un refresh est déjà en cours, on retourne la promesse existante.
      // Tous les callers concurrents (ex: clic bouton + useEffect sur triggerSave qui appelle
      // onSave(true) en parallèle) attendent alors le même refresh effectif.
      // C'est essentiel pour éviter qu'un second appel ne relâche prématurément le premier
      // (= bouton qui redevient cliquable avant la fin du refresh).
      if (refreshDeferredRef.current) {
        return refreshDeferredRef.current.promise;
      }
      let resolve!: () => void;
      const promise = new Promise<void>((res) => {
        resolve = res;
      });
      const deferred = { resolve, promise };
      refreshDeferredRef.current = deferred;

      // Garde-fou : si pour une raison quelconque getRows n'est jamais appelé
      // (ex: grid non prête, condition particulière de refreshData), on résout après 10s
      // pour ne pas figer indéfiniment le bouton.
      setTimeout(() => {
        if (refreshDeferredRef.current === deferred) {
          deferred.resolve();
          refreshDeferredRef.current = null;
        }
      }, 10000);

      return promise;
    }, []);

    const onSave = useCallback(
      async (skipApiPendingref?: boolean) => {
        if (!saveRows || !dirtyRows) {
          return;
        }
        track("datatable::save", { status: "started" });

        if (isApiPendingRef.current === true && !skipApiPendingref) {
          track("datatable::save", { status: "spam" });
          return Promise.resolve();
        } else {
          isApiPendingRef.current = true;
        }

        return saveRows(Object.values(dirtyRows))
          .then((saved) => {
            // On crée le deferred AVANT de déclencher le refresh, afin que getRows
            // (appelé de façon asynchrone par AG Grid) puisse le résoudre.
            const refreshPromise = createRefreshDeferred();

            // Si la sauvegarde a été effectuée, on lance le refresh des données
            // Sinon on stoppe l'édition pour lancer dans l'ordre le wvi (s'il existe) puis la savegarde
            if (saved) {
              if (keepDirty && setKeepDirty) {
                // setKeepDirty lance un refresh et s'assure de ne pas garder les changments en local
                setKeepDirty(false);
              } else {
                refreshData();
              }
              track("datatable::save", { status: "success" });

              onAfterSaveDatatable && onAfterSaveDatatable();
              isWaitingForSaveRef.current = false;

              // On retourne la promesse pour que le caller (ex: ButtonWithLoader)
              // attende la fin réelle du refresh avant de réactiver le bouton.
              return refreshPromise;
            } else {
              isWaitingForSaveRef.current = true;
              gridRef?.current?.api?.stopEditing();
              return refreshPromise;
            }
          })
          .catch((e) => {
            isWaitingForSaveRef.current = false;
            track("datatable::save", { status: "error" });
            console.error("error during save");
            console.error(e);
          })
          .finally(() => {
            isApiPendingRef.current = false;
          });
      },
      [
        saveRows,
        dirtyRows,
        keepDirty,
        setKeepDirty,
        onAfterSaveDatatable,
        refreshData,
        gridRef,
        isApiPendingRef,
        createRefreshDeferred,
      ],
    );

    const beginAdd = useCallback(
      (position: "START" | "END" | number, initialValue?: Record<string, any>) => {
        if (!onAdd) {
          return;
        }
        const started = onAdd(position, initialValue);
        // false : une cellule est encore en édition, on temporise le preRecord
        if (started === false) {
          pendingAddRef.current = { position, initialValue };
          isStoppingEditForAddRef.current = true;
          gridRef?.current?.api?.stopEditing();
          return;
        }
        isStoppingEditForAddRef.current = false;
        pendingAddRef.current = null;
      },
      [onAdd, gridRef],
    );

    const onCellEditingStopped = useCallback(
      (event: CellEditingStoppedEvent<Pojo>) => {
        // Test si la valeur a été modifiée, si oui on ne fait rien car le onChange gère
        if (event.newValue !== event.oldValue) {
          return;
        }
        // Sinon, on teste waitForSaveRef; si elle est true, on appelle la sauvegarde
        if (isWaitingForSaveRef.current === true) {
          onSave(true);
        }
        // Valeur inchangée : le valueSetter ne part pas toujours, on relance l'ajout en attente
        if (isStoppingEditForAddRef.current === true && pendingAddRef.current) {
          const pending = pendingAddRef.current;
          beginAdd(pending.position, pending.initialValue);
        }
      },
      [onSave, isWaitingForSaveRef, isStoppingEditForAddRef, beginAdd],
    );

    useEffect(() => {
      if (triggerSave && triggerSave > 0) {
        onSave(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerSave]);

    // Début d'une tentative d'insert.
    useEffect(() => {
      if (addIntent) {
        beginAdd(addIntent.position, addIntent.initialValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIntent]);

    // Tentative d'insert confirmée.
    useEffect(() => {
      if (
        triggerAdd &&
        triggerAdd > 0 &&
        isStoppingEditForAddRef.current &&
        pendingAddRef.current
      ) {
        const pending = pendingAddRef.current;
        beginAdd(pending.position, pending.initialValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerAdd]);

    const onDelete = useCallback(async () => {
      if (!deleteRows) {
        return;
      }

      if (isApiPendingRef.current === true) {
        track("datatable::delete", { status: "spam" });
        return Promise.resolve();
      } else {
        isApiPendingRef.current = true;
      }

      try {
        const selectedRowsToDelete = selectedRows.filter((r) => r.id != null && r.id !== undefined);
        if (selectAll) {
          await deleteRows({
            selectedRows: [],
            selectedIndexes: [],
            selectedAll: true,
            currentFilter,
            filterBar,
          });
        } else {
          // Récupère sur la gridRef les indexes des lignes sélectionnées
          let selectedIndexes: number[] = [];
          if (gridRef && gridRef.current && gridRef.current.api) {
            const selectedNodes = gridRef.current.api.getSelectedNodes();
            selectedIndexes = selectedNodes
              .map((node) => node.rowIndex ?? -1)
              .filter((idx) => idx !== -1);
          }
          await deleteRows({ selectedRows: selectedRowsToDelete, selectedIndexes });
        }

        // Cf. onSave : on crée le deferred AVANT de déclencher le refresh pour pouvoir
        // attendre sa fin effective via getRows.
        const refreshPromise = createRefreshDeferred();

        if (selectedRowsToDelete.length > 0) {
          if (keepDirty && setKeepDirty) {
            setKeepDirty(false);
          } else {
            refreshData();
          }

          onAfterDeleteDatatable && onAfterDeleteDatatable();
        } else {
          // Applique une transaction sur la grid qui enlève les lignes sélectionnées
          if (gridRef && gridRef.current && gridRef.current.api && selectedRows.length > 0) {
            gridRef.current.api.applyServerSideTransactionAsync({
              remove: selectedRows,
            });
            isApiPendingRef.current = false;
          }
        }

        setSelectAllWithParent(false);

        await refreshPromise;
      } catch (e) {
        track("datatable::delete", { status: "error" });
        console.error("error during delete");
        console.error(e);
      } finally {
        isApiPendingRef.current = false;
      }
    }, [
      currentFilter,
      deleteRows,
      filterBar,
      keepDirty,
      onAfterDeleteDatatable,
      refreshData,
      selectAll,
      selectedRows,
      setKeepDirty,
      setSelectAllWithParent,
      createRefreshDeferred,
    ]);

    const getMainMenuItems = useCallback(
      (params: GetMainMenuItemsParams) => {
        const mapResetColumnsItem = (item: string | MenuItemDef): string | MenuItemDef => {
          if (item !== "resetColumns") {
            return item;
          }
          return {
            name: localeText.resetColumns,
            action: () => {
              if (currentFocus?.focusId && onFocusChange) {
                onFocusChange(currentFocus.focusId);
              }
            },
          };
        };
        const labelTriBloqué = t("commun_datatable_tri_bloque");
        const labelTriMultiple = t("commun_datatable_tri_multiple");
        return [
          hasDirtyRows ? { name: labelTriBloqué, disabled: true } : params.defaultItems[0],
          hasDirtyRows ? { name: labelTriBloqué, disabled: true } : params.defaultItems[1],
          {
            name: labelTriMultiple,
            disabled: true,
          },
          ...params.defaultItems.slice(2).map(mapResetColumnsItem),
        ] as GetMainMenuItemsParams["defaultItems"];
      },
      [currentFocus?.focusId, localeText.resetColumns, onFocusChange, hasDirtyRows, t],
    );

    // Un IR s'appuie sur une vue : ni sa table ni ses colonnes n'existent dans le métamodèle
    // (syjTables/syjColumn), donc aucune entrée du menu contextuel ne peut être résolue.
    const isInteractiveReport = definition.type === "INTERACTIVE_REPORT";

    const getContextMenuItems = useCallback(
      async (params: GetContextMenuItemsParams) => {
        if (isInteractiveReport) {
          return ["copy", "copyWithHeaders"];
        }
        const rowData = params.node?.data;
        // Ligne mère (groupe) : ses données sont synthétiques (id = concaténation des clés de
        // groupe, pas de tableName, valeurs d'agrégat) → aucune entrée ne peut être résolue.
        if (!rowData || params.node?.group) {
          return [];
        }
        const id = rowData.id;

        const contextColumnName = (params.column as any)?.colId;
        const tableName = rowData?.tableName;
        const defCol = definition.columns.find((col) => col.column === contextColumnName);
        const joinTableName = defCol?.joinTableName;
        // Si la valeur est un objet, on prend l'id de l'objet
        // Sinon ET que la table de jointure est définie, on prend l'id de la table de jointure
        const joinId =
          typeof params?.value === "object"
            ? params?.value?.id
            : joinTableName
              ? params.value
              : null;

        const menuItems: (Record<string, any> | "separator")[] = [];
        if (joinId) {
          const res = await getProcessusDefinitionColumn(sjmoCode, tableName, defCol?.column);

          const traitements = res.data.filter((it) => it.type === "traitement");
          const navigations = res.data.filter((it) => it.type === "navigation");
          const editions = res.data.filter((it) => it.type === "edition");

          if (traitements.length > 0) {
            menuItems.push({
              name: t("commun_traitement"),
              subMenu: traitements.map((traitement) => ({
                key: traitement.id,
                name: traitement.label,
                menuItem: MenuItem,
                menuItemParams: {
                  buttonValue: traitement.label,
                  definition: traitement,
                  selected: joinId ? params.value : rowData,
                  sjmoCode,
                  tableName: joinTableName ?? tableName,
                  setShowConfirmModal,
                },
              })),
            });
          }

          if (navigations.length > 0) {
            menuItems.push({
              name: t("commun_navigation"),
              subMenu: navigations.map((navigation) => ({
                key: navigation.id,
                name: navigation.label,
                menuItem: MenuItem,
                menuItemParams: {
                  buttonValue: navigation.label,
                  definition: navigation,
                  selected: joinId ? params.value : rowData,
                  sjmoCode,
                  tableName: joinTableName ?? tableName,
                  setShowConfirmModal,
                },
              })),
            });
          }

          if (editions.length > 0) {
            menuItems.push({
              name: t("commun_editions"),
              subMenu: editions.map((edition) => ({
                key: edition.id,
                name: edition.label,
                menuItem: MenuItem,
                menuItemParams: {
                  buttonValue: edition.label,
                  definition: edition,
                  selected: joinId ? params.value : rowData,
                  sjmoCode,
                  tableName: joinTableName ?? tableName,
                  setShowConfirmModal,
                },
              })),
            });
          }
          menuItems.push("separator");
        }

        menuItems.push({
          key: "miniExpert",
          name: t("commun_miniExpert"),
          menuItem: MiniExpertMenuItem,
          menuItemParams: {
            sjmoCode,
            tableName: joinTableName ?? tableName,
            id: joinId ?? id,
          },
        });

        menuItems.push({
          key: "info",
          name: t("commun_information"),
          menuItem: InfoMenuItem,
          menuItemParams: {
            tableName,
            joinId,
            id,
            contextColumnName,
          },
        });

        return menuItems;
      },
      [definition.columns, sjmoCode, t],
    );

    const rowClassRules = useMemo<RowClassRules>(() => {
      const idFromDirtyRows = Object.values(dirtyRows ?? {}).map((row) => row.id ?? row.uuid);
      return {
        [`${tw("border-l-2 border-blue-500")}`]: (params: any) => {
          if (!params.data) return false;
          return (
            idFromDirtyRows.includes(params.data.id) || idFromDirtyRows.includes(params.data.uuid)
          );
        },
        [`${tw("border-t-2 border-b-0 border-green-500")}`]: (params: any) =>
          params.node.rowPinned === "bottom" && params.data,
        [`${tw("!bg-blue-100")}`]: (params: any) =>
          params.data?.id != null && params.data.id === lastFocusedRowIdRef.current,
      };
      // rowClassRefreshTick force la réévaluation après écriture de lastFocusedRowIdRef.current
      // (ref, non réactive) ; lastFocusedRowIdRef volontairement absent des deps.
    }, [dirtyRows, rowClassRefreshTick]);

    const getRowStyle = useCallback(
      (params: any) => {
        if (!params.data || !effectiveHighlights || effectiveHighlights.length === 0) {
          return undefined;
        }

        const lineHighlights = effectiveHighlights
          .filter((h) => h.isActive && h.typeHighlight === "line")
          .sort((a, b) => b.sequence - a.sequence);

        for (const highlight of lineHighlights) {
          const column = allColumnRef[highlight.column];
          const typeCompo = column?.typeCompo ?? "TEXT";

          if (isHighlighted(typeCompo, params.data, highlight)) {
            const style: Record<string, string> = {};
            if (highlight.backgroundColor) {
              style.backgroundColor = highlight.backgroundColor;
            }
            if (highlight.color) {
              style.color = highlight.color;
            }
            return style;
          }
        }

        return undefined;
      },
      [effectiveHighlights, allColumnRef],
    );

    const onRowCliked = useCallback(
      (params: RowClickedEvent) => {
        if (onRowClickParent) {
          // on ne clique pas sur les groupes
          !params.node.group && onRowClickParent(params.data.id);
        } else if (params.data && !params.node.group) {
          // Même logique que getRows : on contextualise dès qu'on a une ligne donnée,
          // sans exiger `id` (certaines vues / IR n'ont que uuid ou des clés métier).
          // Exiger `id` envoyait contextualize(null) et nullifiait les valueParent enfants.
          lastFocusedRowIdRef.current = params.data.id ?? params.data.uuid ?? null;
          bumpRowClassRefresh((tick) => tick + 1);
          reduxDispatch(
            contextualize(sjmoCode, tableName, ctrlkey, flatDataFromPopjo(params.data) as Pojo),
          );
        }
      },
      [ctrlkey, onRowClickParent, reduxDispatch, sjmoCode, tableName],
    );

    // suivi continu de la position de scroll vertical
    const onBodyScroll = useCallback((event: BodyScrollEvent) => {
      if (event.direction === "vertical") {
        scrollTopRef.current = event.top;
      }
    }, []);

    // lors d'un changement de tri, AG Grid recharge les données : on mémorise le scroll
    // pour le restaurer une fois les nouvelles données chargées (cf. getRows).
    const onSortChanged = useCallback(
      (event: SortChangedEvent) => {
        if (hasDirtyRows || event.source === "api") {
          return;
        }
        const visibleColIds = getVisibleColumnIds(definition.columns);
        effectiveSortRef.current = calculateSortValues(
          effectiveSortRef.current ?? {},
          visibleColIds,
          event.columns?.map((c) => ({
            colId: c.getColId() as string,
            sort: c.getSort() as "asc" | "desc" | null | undefined,
            sortIndex: c.getSortIndex(),
          })) ?? [],
        );
        pendingScrollRestoreRef.current = scrollTopRef.current;
      },
      [definition.columns, hasDirtyRows],
    );

    const onColumnPinned = useCallback(
      (event: ColumnPinnedEvent) => {
        const currentPinned = event.api
          .getColumnState()
          .filter(
            (c) => c.pinned !== null && c.pinned !== undefined && !isColumnSelectionCol(c.colId),
          )
          .map((c) => ({ colId: c.colId, pinned: c.pinned }));
        onChangePinned?.(currentPinned);
      },
      [onChangePinned],
    );

    useEffect(() => {
      if (gridPadding === "large" && maxRowHeight > MIN_ROW_HEIGHT) {
        gridRef.current?.api?.resetRowHeights();
      }
    }, [maxRowHeight, gridPadding, gridRef]);

    useEffect(() => {
      function reloadSatelliteExists() {
        let ids: string[] = [];

        gridRef.current?.api?.forEachNode((node) => {
          ids.push(node.data.id);
        });

        existList({ tableName, ids }).then((satelliteExistResponse) => {
          setExistSatellite((x) => ({ ...x, ...satelliteExistResponse.data }));
        });
      }

      let event = ctrlkey + "--satellite";
      window.addEventListener(event, reloadSatelliteExists);
      return () => {
        window.removeEventListener(event, reloadSatelliteExists);
      };
    }, [ctrlkey, gridRef, tableName]);

    const getRowHeight = useCallback(
      (params: RowHeightParams<Pojo>) => {
        if (params.node.rowPinned === "bottom") {
          return MIN_ROW_HEIGHT;
        }
        return maxRowHeight;
      },
      [maxRowHeight],
    );

    return (
      <HtmlPreviewProvider>
        <AutoSizer disableWidth>
          {({ width, height }) => (
            <ProcessusProvider
              sjmoCode={sjmoCode}
              tableName={tableName}
              selected={flatSelectedRows as Pojo[]}
            >
              <div ref={toolbarWrapperRef}>
                <AgTableToolbar
                  sjmoCode={sjmoCode}
                  tableName={tableName}
                  processOrigine={processOrigine}
                  ctrlkey={ctrlkey}
                  sjpaId={sjpaId}
                  focus={definition.focus}
                  selectedFocus={definition.selectedFocus}
                  selectedRows={flatSelectedRows as Pojo[]}
                  filterable={
                    hasDirtyRows
                      ? false
                      : (definition.focus.find((f) => f.focusId === definition.selectedFocus)
                          ?.filterable ?? false)
                  }
                  filters={
                    definition.focus.find((f) => f.focusId === definition.selectedFocus)?.filters ??
                    []
                  }
                  filterBar={filterBar}
                  hideFilterBar={hideFilterBar}
                  columns={definition.columns}
                  currentFilter={currentFilter}
                  lastFilterFetched={currentFilter}
                  columnFilter={columnFilter}
                  gridPadding={gridPadding}
                  groupByState={groupByState ?? { columns: [], functionList: [] }}
                  defaultOpenBreakRows={defaultOpenBreakRows}
                  dirtyRows={dirtyRows ?? {}}
                  selectAll={selectAll}
                  rowCount={rowCount}
                  selectedNumber={selectAll ? rowCount : selectedNumber}
                  typeComposant={definition.type}
                  highlightColumns={definition.columns.filter(
                    (c) => c.typeCompo !== TypeSimpleComponent.INPUT,
                  )}
                  highlights={effectiveHighlights}
                  isGroupable={
                    Object.values(dirtyRows ?? {}).filter(
                      (row) => row.id === undefined || row.id === null,
                    ).length === 0
                  }
                  ctrlKey={ctrlkey}
                  setSelectAll={setSelectAllWithParent}
                  setGridPadding={setGridPadding}
                  refresh={(unselect: boolean) => {
                    if (keepDirty && setKeepDirty) {
                      setKeepDirty(false);
                    } else {
                      refreshData();
                    }
                    if (unselect) {
                      setSelectedRows([]);
                      setSelectedRowsParent && setSelectedRowsParent([]);
                      gridRef.current?.api?.deselectAll();
                    }
                  }}
                  onFilterChange={({
                    filterBarId,
                    startDate,
                    endDate,
                    filterBarDefaultDtFilter,
                  }) => {
                    if (hasDirtyRows) {
                      return;
                    }
                    keepDirty && setKeepDirty && setKeepDirty(false);
                    setFilterBar({
                      filterBarId,
                      startDate,
                      endDate,
                      filterBarDefaultDtFilter,
                    });
                  }}
                  onFocusChange={onFocusChange}
                  onSaveFocusPerso={
                    onSaveFocusPerso
                      ? (callback: () => void) =>
                          onSaveFocusPerso(
                            {
                              currentFilter: currentLocalFilter,
                              currentOrderBy: currentLocalOrderBy,
                              currentPinned: pinnedState,
                              currentHighlight: effectiveHighlights,
                              currentAggregates: aggregates,
                              currentGroupBy: groupByState,
                              defaultOpenBreakRows,
                            },
                            callback,
                          )
                      : undefined
                  }
                  onRazFocusPerso={onRazFocusPerso}
                  onSave={
                    saveRows && (currentFocus?.updatatable || currentFocus?.insertable)
                      ? onSave
                      : undefined
                  }
                  onDelete={deleteRows && currentFocus?.deletable ? onDelete : undefined}
                  filterAllFields={filterAllFields}
                  setFilterAllFields={(filterAllFieldsValue: string | undefined) => {
                    if (hasDirtyRows) {
                      return;
                    }
                    keepDirty && setKeepDirty && setKeepDirty(false);
                    setFilterAllFields(filterAllFieldsValue);
                  }}
                  setGroupBy={(
                    columns: string[],
                    functionList: GroupByFunctionParameter[],
                    nextDefaultOpenBreakRows: boolean,
                  ) => {
                    onChangeGroupBy?.({
                      columns,
                      functionList,
                    });
                    onChangeDefaultOpenBreakRows?.(nextDefaultOpenBreakRows);
                  }}
                  aggregates={aggregates}
                  onChangeAggregates={(next: AggregateProperties[]) => {
                    onChangeAggregates?.(next);
                  }}
                  onAdd={insertable ? beginAdd : undefined}
                  onValidateHighlights={onValidateHighlights}
                  onDeleteHighlights={onDeleteHighlights}
                  onClearHighlights={onClearHighlights}
                  clearColumnFilter={() => {
                    if (hasDirtyRows) {
                      return;
                    }
                    // Vider tous les filtres de colonnes
                    gridRef?.current?.api?.setFilterModel(null);
                    // Pas besion de mettre à jour currentFilter on lance le refresh des données
                  }}
                  onAfterSaveProcess={onAfterSaveDatatable}
                />
              </div>
              <RowHeightContext.Provider value={registerHeight}>
                <div style={{ width: width, height: height - toolbarHeight }}>
                  <div style={gridStyle} ref={gridWrapperRef}>
                    <AgGridReact<Pojo>
                      ref={ref}
                      columnDefs={columnDefs as any}
                      domLayout="normal"
                      rowModelType="serverSide"
                      //TODO stopEditingWhenCellsLoseFocus={true}  Option qui permet de stopper l'édition lorsque l'on clique en dehors de la grille, mais ça fou en l'air le switch
                      autoGroupColumnDef={autoGroupColumnDef}
                      isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
                      rowClassRules={rowClassRules}
                      getRowStyle={getRowStyle}
                      groupAllowUnbalanced={true}
                      cacheBlockSize={25} // Nombre d'éléments par requête
                      blockLoadDebounceMillis={10} // Délai avant de déclencher une nouvelle requête
                      localeText={localeText}
                      noRowsOverlayComponent={NoRowsOverlay}
                      noRowsOverlayComponentParams={{ message: t("commun_aucune_donnée") }}
                      rowSelection={selectAll ? undefined : ROW_SELECTION_MODE}
                      getRowHeight={getRowHeight}
                      selectionColumnDef={SELECTION_COLUMN_DEF}
                      singleClickEdit={true}
                      pinnedBottomRowData={pinnedBottomRowData}
                      getRowId={getRowId ?? defaultGgetRowId}
                      getMainMenuItems={getMainMenuItems}
                      getContextMenuItems={getContextMenuItems as any}
                      onSelectionChanged={selectAll ? undefined : onSelectionChanged}
                      onGridReady={onGridReady}
                      onColumnMoved={onColumnMoved}
                      onColumnVisible={onColumnVisible}
                      onRowClicked={onRowCliked}
                      onColumnResized={onColumnResized}
                      onCellKeyDown={onCellKeyDown}
                      onColumnPinned={onColumnPinned}
                      onCellEditingStopped={onCellEditingStopped}
                      onBodyScroll={onBodyScroll}
                      onSortChanged={onSortChanged}
                      processCellForClipboard={processCellForClipboard}
                    />
                  </div>
                </div>
              </RowHeightContext.Provider>
            </ProcessusProvider>
          )}
        </AutoSizer>
        {showConfirmModal?.show && showConfirmModal?.position && (
          <ProcessusProvider
            sjmoCode={sjmoCode}
            tableName={tableName}
            selected={showConfirmModal.selected}
          >
            <ConfirmationModal
              id={showConfirmModal.id}
              launchProcess={(editionType?: "rapide" | "apercu" | undefined) => {
                showConfirmModal.launchProcess(editionType, showConfirmModal.selected);
                setShowConfirmModal(null);
              }}
              close={() => {
                showConfirmModal.close();
                setShowConfirmModal(null);
              }}
              top={showConfirmModal.position.x}
              left={showConfirmModal.position.y}
              buttonRef={showConfirmModal.buttonRef}
            />
          </ProcessusProvider>
        )}
      </HtmlPreviewProvider>
    );
  },
);

export default AgGridContainer;
